import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useIncomeSources } from '../hooks/useScoring';
import type { ClientIncomeSource } from '@/types/scoring.types';

interface IncomeSourcesListProps {
  directoryId: string;
}

const FREQUENCY_COLORS: Record<string, string> = {
  WEEKLY: 'text-sky-400',
  BIWEEKLY: 'text-violet-400',
  MONTHLY: 'text-teal-400',
  DAILY: 'text-indigo-400',
  IRREGULAR: 'text-slate-400',
};

function variantFor(c: string): 'success' | 'warning' | 'danger' | 'default' {
  switch (c) {
    case 'PRIMARY': return 'success';
    case 'SECONDARY': return 'warning';
    case 'SPORADIC': return 'danger';
    default: return 'default';
  }
}

export function IncomeSourcesList({ directoryId }: IncomeSourcesListProps) {
  const { data, isLoading, isError } = useIncomeSources(directoryId);

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Skeleton height={32} variant="rectangular" />
        <Skeleton height={32} variant="rectangular" />
      </div>
    );
  }

  if (isError) {
    return <p className="text-xs text-destructive">Error al cargar fuentes de ingreso.</p>;
  }

  const sources: ClientIncomeSource[] = data?.sources ?? [];

  if (sources.length === 0) {
    return <p className="text-xs text-muted-foreground">Sin fuentes de ingreso detectadas.</p>;
  }

  return (
    <ul className="space-y-1">
      {sources.map((s, idx) => {
        const freqColor = FREQUENCY_COLORS[s.detected_frequency] ?? 'text-muted-foreground';
        return (
          <li
            key={idx}
            className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Badge variant={variantFor(s.classification)}>{s.classification}</Badge>
              <span className={`font-semibold ${freqColor}`}>{s.detected_frequency}</span>
              <span className="truncate text-muted-foreground">
                · {s.total_deposits_90d} dep / 90d
              </span>
            </div>
            <span className="shrink-0 tabular-nums text-foreground">
              ${(s.average_amount_usd ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0 })} USD prom.
            </span>
          </li>
        );
      })}
    </ul>
  );
}
